import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../common/Modal';
import MavisMeetingDirectory from './MavisMeetingDirectory';
import type { MeetingDirectoryMeta } from './meetingDirectoryModel';
import { Video, Pause, Play, UserMinus, UserPlus, Loader2, Download, ChevronDown, X } from 'lucide-react';
import {
  MavisApiError,
  controlMeetingCapture,
  createMeeting,
  getMeetingTranscript,
  inviteMeetingBot,
  listMeetings,
  refreshMeetingBotStatus,
  reviewMeeting,
} from '../../lib/mavisApi';
import { useAuth } from '../../contexts/AuthContext';
import type { MavisMeetingSession, MavisLiveEvent } from '../../types/mavis';
import { meetingStatus, transcriptRows } from './meetingTranscript';
import MavisCallReview from './MavisCallReview';
import type { ReviewData } from './callReviewModel';

interface Props {
  sessions: MavisMeetingSession[];
  opportunityId: string;
  accountId: string | null;
  onSessionAdded: (session: MavisMeetingSession) => void;
  onSessionUpdated: (session: MavisMeetingSession) => void;
}

type CaptureCommand = 'invite' | 'pause' | 'resume' | 'leave' | 'reinvite';

const MEETING_ACTION_FAILED = 'This meeting action could not be completed. Please retry.';
const SUPPORTED_MEETING_HOST = /(^|\.)(zoom\.us|zoom\.com|meet\.google\.com|teams\.microsoft\.com|teams\.live\.com)$/;
const STATUS_CHECK_INTERVAL_MS = 10_000;
const TRANSCRIPT_POLL_INTERVAL_MS = 2_000;

const ended = (session: MavisMeetingSession) => ['completed', 'ended', 'left', 'failed'].includes(session.status);

/** User-facing message for a failed meeting request (the API's own message when it sent one). */
export function meetingErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof MavisApiError) {
    return typeof err.body?.error === 'string' ? err.body.error : MEETING_ACTION_FAILED;
  }
  return err instanceof Error ? err.message : fallback;
}

function isSupportedMeetingUrl(value: string): boolean {
  try {
    const meeting = new URL(value);
    return meeting.protocol === 'https:' && SUPPORTED_MEETING_HOST.test(meeting.hostname);
  } catch {
    return false;
  }
}

function speakerName(row: { participant_name?: string | null; speaker_label?: string | null }): string {
  return row.participant_name || (row.speaker_label ? `Speaker ${row.speaker_label}` : 'Unknown speaker');
}

function formatOffset(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

function saveTranscript(text: string, title: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = title.replace(/[^\w -]/g, '') + '.txt';
  link.click();
  URL.revokeObjectURL(url);
}

function DismissibleAlert({ message, label, onDismiss }: { message: string; label: string; onDismiss: () => void }) {
  return (
    <div role="alert" className="flex items-start justify-between gap-3 text-sm text-red-700">
      <p>{message}</p>
      <button type="button" className="btn-ghost !p-1 shrink-0" aria-label={label} onClick={onDismiss}>
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}

interface SessionWorkspaceProps {
  session: MavisMeetingSession;
  onUpdated: (s: MavisMeetingSession) => void;
  liveInsights?: ReactNode;
  playbook?: ReactNode;
  initialTime?: number;
  initialBotId?: string;
}

export function MavisSessionWorkspace({
  session,
  onUpdated,
  liveInsights,
  playbook,
  initialTime,
  initialBotId,
}: SessionWorkspaceProps) {
  const { user } = useAuth();

  // Provider failures can be dismissed; the dismissal is remembered per failure fingerprint.
  const errorStorageKey = `heymakr:mavis-error:${user?.id ?? 'anonymous'}:${session.org_id}:${session.id}`;
  const providerError = session.status === 'failed' && typeof session.recall_bot_metadata?.status_msg === 'string'
    ? session.recall_bot_metadata.status_msg
    : '';
  const errorFingerprint = JSON.stringify([session.recall_bot_id, session.recall_bot_metadata?.status_at, providerError]);
  const [dismissedError, setDismissedError] = useState<{ key: string; fingerprint: string | null } | null>(() => {
    try {
      return { key: errorStorageKey, fingerprint: window.localStorage.getItem(errorStorageKey) };
    } catch {
      return null;
    }
  });
  const dismissProviderError = () => {
    setDismissedError({ key: errorStorageKey, fingerprint: errorFingerprint });
    try {
      window.localStorage.setItem(errorStorageKey, errorFingerprint);
    } catch {
      /* Dismissal still works when browser storage is unavailable. */
    }
  };
  const providerErrorDismissed = dismissedError?.key === errorStorageKey && dismissedError.fingerprint === errorFingerprint;

  const [finals, setFinals] = useState<MavisLiveEvent[]>([]);
  const [partials, setPartials] = useState<MavisLiveEvent[]>([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [savedText, setSavedText] = useState('');
  const [review, setReview] = useState<ReviewData | null>(null);
  const [recordingMessage, setRecordingMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [follow, setFollow] = useState(true);

  const cursor = useRef<string | null>(null);
  const fetching = useRef(false);
  const mounted = useRef(true);
  const current = useRef(session);
  const update = useRef(onUpdated);
  const viewport = useRef<HTMLDivElement>(null);
  const lastStatusCheck = useRef(0);
  const operation = useRef(false);
  current.current = session;
  update.current = onUpdated;

  // Pages through the transcript from the last cursor until the server reports no more events.
  const snapshot = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    try {
      let more = true;
      while (more && mounted.current) {
        const data = await getMeetingTranscript(session.id, cursor.current);
        if (!mounted.current) return;
        cursor.current = data.cursor;
        setFinals((previous) => {
          const rows = new Map(previous.map((row) => [row.id, row]));
          for (const row of data.events) rows.set(row.id, row);
          return [...rows.values()];
        });
        setPartials(data.partials);
        update.current(data.session);
        more = data.more;
      }
      setError('');
    } catch (err) {
      if (mounted.current) setError(meetingErrorMessage(err, 'Transcript connection interrupted. Retrying…'));
    } finally {
      fetching.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [session.id]);

  useEffect(() => {
    mounted.current = true;

    const refreshBotStatus = async (active: MavisMeetingSession) => {
      lastStatusCheck.current = Date.now();
      try {
        const result = await refreshMeetingBotStatus(active.id);
        if (mounted.current) update.current(result.session);
      } catch (err) {
        if (mounted.current) setError(meetingErrorMessage(err, 'Could not refresh meeting status.'));
      }
    };

    const poll = async () => {
      if (document.visibilityState !== 'visible') return;
      await snapshot();
      const active = current.current;
      const statusCheckDue = Date.now() - lastStatusCheck.current > STATUS_CHECK_INTERVAL_MS;
      if (mounted.current && active.recall_bot_id && !ended(active) && !operation.current && statusCheckDue) {
        await refreshBotStatus(active);
      }
    };

    void poll();
    const timer = setInterval(() => {
      if (!ended(current.current)) void poll();
    }, TRANSCRIPT_POLL_INTERVAL_MS);
    const wake = () => {
      void poll();
    };
    document.addEventListener('visibilitychange', wake);
    return () => {
      mounted.current = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [snapshot]);

  const rows = transcriptRows(finals, ended(session) ? [] : partials);

  useEffect(() => {
    if (follow && viewport.current) viewport.current.scrollTop = viewport.current.scrollHeight;
  }, [finals, partials, follow]);

  const run = async (command: CaptureCommand) => {
    if (operation.current) return;
    operation.current = true;
    setBusy(command);
    setError('');
    try {
      if (command === 'invite') await inviteMeetingBot(session.id);
      else await controlMeetingCapture(session.id, command);
      await snapshot();
    } catch (err) {
      setError(meetingErrorMessage(err, 'Meeting action failed.'));
    } finally {
      operation.current = false;
      setBusy('');
    }
  };

  const artifacts = async () => {
    if (operation.current) return;
    operation.current = true;
    setBusy('recording');
    setError('');
    try {
      const data = await reviewMeeting(session.id);
      if (!mounted.current) return;
      if (data.recordings.length) setReview(data as ReviewData);
      if (data.savedTranscript) setSavedText(data.savedTranscript);
      setRecordingMessage(data.recordings.length
        ? ''
        : 'The recording is still processing or was not captured. Captured transcript remains available.');
      if (data.session) onUpdated(data.session);
      await snapshot();
    } catch (err) {
      setError(meetingErrorMessage(err, 'Recording unavailable.'));
    } finally {
      operation.current = false;
      setBusy('');
    }
  };

  useEffect(() => {
    if (ended(session) && session.recall_bot_id) void artifacts();
    else setReview(null);
  }, [session.id, session.recall_bot_id, session.status]);

  const onTranscriptScroll = () => {
    const el = viewport.current;
    if (el) setFollow(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
  };

  const paused = Boolean(session.recall_bot_metadata?.capture_paused);
  const inMeeting = ['recording', 'active', 'joined'].includes(session.status);
  const leaving = Boolean(session.recall_bot_metadata?.leave_requested) && !ended(session);
  const recording = session.status === 'recording' && !paused && !leaving;
  const canReinvite = session.recall_bot_id
    && (session.status === 'left' || (session.status === 'failed' && session.recall_bot_metadata?.status_code === 'kicked'));
  const text = rows
    .filter((row) => row.event_type !== 'transcript_partial')
    .map((row) => `${speakerName(row)}: ${row.content_text}`)
    .join('\n\n');
  const statusDotClass = recording ? 'bg-red-500 motion-safe:animate-pulse' : paused ? 'bg-amber-500' : 'bg-slate-400';
  const emptyTranscriptMessage = paused
    ? 'Recording is paused.'
    : ended(session)
      ? 'No transcript captured yet. Check for the completed transcript below.'
      : session.recall_bot_id
        ? 'Speech will appear here after MAVIS joins and recording begins.'
        : 'Start the session to invite MAVIS and capture the transcript.';

  const transcript = (
    <div className={liveInsights ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(320px,.75fr)] gap-5' : ''}>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">{ended(session) ? 'Call transcript' : 'Live transcript'}</h3>
          {text && (
            <button
              className="btn-ghost !text-xs"
              onClick={() => saveTranscript(session.recall_bot_metadata?.previous_bot_ids ? text : savedText || text, session.meeting_title)}
            >
              <Download className="w-4 h-4" />
              Download transcript
            </button>
          )}
        </div>
        <div
          ref={viewport}
          onScroll={onTranscriptScroll}
          className="max-h-[60vh] min-h-32 overflow-y-auto rounded-xl bg-slate-50 p-4 space-y-4"
          tabIndex={0}
          aria-label="Meeting transcript"
        >
          {loading && <p className="text-sm text-slate-500">Loading transcript…</p>}
          {!loading && rows.length === 0 && !savedText && <p className="text-sm text-slate-500">{emptyTranscriptMessage}</p>}
          {rows.map((row) => (
            <div key={row.id} className={row.event_type === 'transcript_partial' ? 'opacity-70' : ''}>
              <div className="flex gap-2 items-baseline">
                <span className="text-sm font-semibold text-slate-800">{speakerName(row)}</span>
                {typeof row.content_json?.relative_start === 'number' && (
                  <span className="text-xs text-slate-500">{formatOffset(row.content_json.relative_start)}</span>
                )}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{row.content_text}</p>
            </div>
          ))}
          {savedText && rows.length === 0 && <p className="text-sm text-slate-700 whitespace-pre-wrap">{savedText}</p>}
        </div>
        {!follow && !ended(session) && (
          <button className="btn-ghost !text-xs mt-2" onClick={() => setFollow(true)}>
            <ChevronDown className="w-4 h-4" />
            Follow live transcript
          </button>
        )}
      </div>
      {liveInsights}
    </div>
  );

  return (
    <div className="border-t border-slate-100 p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="status" aria-live="polite" className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${statusDotClass}`} />
          {meetingStatus(session)}
          {recording && <span className="text-red-600 text-xs">Recording</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!session.recall_bot_id && session.status !== 'joining' && (
            <button className="btn-primary !py-2 !text-sm" disabled={Boolean(busy)} onClick={() => void run('invite')}>
              <UserPlus className="w-4 h-4" />
              Start session
            </button>
          )}
          {inMeeting && !leaving && (
            <button className="btn-secondary !py-2 !text-sm" disabled={Boolean(busy)} onClick={() => void run(paused ? 'resume' : 'pause')}>
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              {paused ? 'Resume recording' : 'Pause recording'}
            </button>
          )}
          {session.recall_bot_id && !ended(session) && !leaving && (
            <button className="btn-secondary !py-2 !text-sm" disabled={Boolean(busy)} onClick={() => void run('leave')}>
              <UserMinus className="w-4 h-4" />
              Remove MAVIS
            </button>
          )}
          {canReinvite && (
            <button className="btn-secondary !py-2 !text-sm" disabled={Boolean(busy)} onClick={() => void run('reinvite')}>
              <UserPlus className="w-4 h-4" />
              Reinvite MAVIS
            </button>
          )}
          {busy && <Loader2 className="w-4 h-4 animate-spin" aria-label="Updating meeting" />}
        </div>
      </div>
      {error && <DismissibleAlert message={error} label="Dismiss action error" onDismiss={() => setError('')} />}
      {providerError && !providerErrorDismissed && (
        <DismissibleAlert message={providerError} label="Dismiss meeting error" onDismiss={dismissProviderError} />
      )}
      {review ? (
        <MavisCallReview
          session={session}
          data={review}
          busy={Boolean(busy)}
          onRefresh={() => void artifacts()}
          playbook={playbook}
          initialTime={initialTime}
          initialBotId={initialBotId}
        />
      ) : (
        transcript
      )}
      {review ? null : ended(session) ? playbook : null}
      {!review && ended(session) && session.recall_bot_id && (
        <div className="space-y-3">
          <button className="btn-secondary !text-sm" disabled={Boolean(busy)} onClick={() => void artifacts()}>
            <Video className="w-4 h-4" />
            View completed transcript and recording
          </button>
          {recordingMessage && <p role="status" className="text-sm text-slate-500">{recordingMessage}</p>}
        </div>
      )}
    </div>
  );
}

export default function MavisMeetingPanel({ sessions, opportunityId, onSessionAdded, onSessionUpdated }: Props) {
  const { organisation, user } = useAuth();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [metadata, setMetadata] = useState<MeetingDirectoryMeta[]>([]);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState(false);
  const starting = useRef(false);

  useEffect(() => {
    let current = true;
    setMetadataLoading(true);
    setMetadata([]);
    setMetadataError(false);
    listMeetings(opportunityId)
      .then((data) => {
        if (current) setMetadata((data.meetings ?? []) as MeetingDirectoryMeta[]);
      })
      .catch(() => {
        if (current) {
          setMetadata([]);
          setMetadataError(true);
        }
      })
      .finally(() => {
        if (current) setMetadataLoading(false);
      });
    return () => {
      current = false;
    };
  }, [opportunityId, sessions.length]);

  const start = async (event: React.FormEvent) => {
    event.preventDefault();
    if (starting.current || !organisation || !user) return;
    if (!isSupportedMeetingUrl(url.trim())) {
      setError('Enter a valid Zoom, Google Meet or Teams meeting URL.');
      return;
    }
    starting.current = true;
    setBusy(true);
    setError('');
    try {
      const { session } = await createMeeting(opportunityId, {
        meeting_url: url.trim(),
        meeting_title: title.trim() || `Meeting ${new Date().toLocaleString()}`,
      });
      if (!session) throw new Error('Could not save the meeting.');
      onSessionAdded(session as MavisMeetingSession);
      await inviteMeetingBot(session.id);
      const fresh = await getMeetingTranscript(session.id);
      onSessionUpdated(fresh.session);
      setUrl('');
      setTitle('');
      setCreating(false);
      navigate(`/app/mavis/${opportunityId}/meetings/${session.id}`, {
        state: { returnTo: `/app/mavis/${opportunityId}?tab=meeting_control` },
      });
    } catch (err) {
      setError(meetingErrorMessage(err, 'Could not start the session.'));
    } finally {
      starting.current = false;
      setBusy(false);
    }
  };

  const closeModal = () => {
    if (!busy) setCreating(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-app-ink">Meetings</h2>
          <p className="text-sm text-app-muted mt-1">Your calls for this opportunity, in one place.</p>
        </div>
        <button className="btn-primary !text-sm" onClick={() => setCreating(true)}>
          <Video className="w-4 h-4" />
          New meeting
        </button>
      </div>
      <MavisMeetingDirectory
        sessions={sessions}
        opportunityId={opportunityId}
        metadata={metadata}
        metadataLoading={metadataLoading}
        metadataError={metadataError}
      />
      <Modal isOpen={creating} onClose={closeModal} title="New meeting" size="md">
        <form onSubmit={(event) => void start(event)} className="space-y-4">
          <div>
            <label className="text-xs text-app-muted block mb-1.5" htmlFor="new-meeting-title">
              Meeting name <span className="text-app-muted">(optional)</span>
            </label>
            <input
              id="new-meeting-title"
              aria-label="Meeting name (optional)"
              className="input-field"
              placeholder="Give this meeting a name"
              value={title}
              maxLength={500}
              onChange={(event) => setTitle(event.target.value)}
              disabled={busy}
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-app-muted block mb-1.5" htmlFor="new-meeting-url">Meeting URL</label>
            <input
              id="new-meeting-url"
              aria-label="Meeting URL"
              type="url"
              required
              className="input-field"
              placeholder="Paste a Zoom, Google Meet or Teams URL"
              value={url}
              maxLength={2000}
              onChange={(event) => setUrl(event.target.value)}
              disabled={busy}
            />
          </div>
          <p className="text-xs text-app-muted">MAVIS will join and capture the transcript when you start.</p>
          {error && (
            <div role="alert" className="flex items-start justify-between gap-3 text-sm text-app-critical">
              <p>{error}</p>
              <button type="button" className="btn-ghost !p-1" aria-label="Dismiss start error" onClick={() => setError('')}>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="flex justify-end">
            <button className="btn-primary !text-sm" disabled={busy || !url.trim()}>
              {busy ? <Loader2 className="w-4 h-4 motion-safe:animate-spin" /> : <Video className="w-4 h-4" />}
              {busy ? 'Starting MAVIS…' : 'Start session'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
